'use client';

import { Fragment, useState } from 'react';
import { FlaskIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Menu, MenuItem } from '@tranquility-ai/theme-components/menu';
import { Modal } from '@tranquility-ai/theme-components/modal';
import {
  useReactiveFeatureFlagValue,
  useReactiveFeatureFlags,
} from '@/app/contexts';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import {
  statusBarIconButtonClassName,
  statusBarIconButtonProps,
} from '../status-bar-icon-button';
import type { PrototypeEntry } from './prototype-entry';
import { usePrototypeEntries } from './prototype-registry';

export type { PrototypeEntry } from './prototype-entry';

/**
 * Status-bar entry for in-progress prototypes. Hidden unless the
 * `design-prototypes` LaunchDarkly flag is on (`designPrototypes` in the SDK).
 * Each enabled demo contributes a load item and a forget item. Choosing load
 * unmounts every other demo, mounts that demo's data, and opens its guide.
 * Only one prototype is mounted.
 */
export function PrototypeMenu({ caseId }: { caseId?: string }) {
  const designPrototypes = useReactiveFeatureFlagValue('designPrototypes');
  const entries = usePrototypeEntries(caseId);
  if (designPrototypes !== true) return null;
  return <PrototypeMenuShell entries={entries} />;
}

function PrototypeMenuShell({ entries }: { entries: PrototypeEntry[] }) {
  const t = useTimePilotT();
  const featureFlags = useReactiveFeatureFlags();
  const [menuOpen, setMenuOpen] = useState(false);
  const [guideId, setGuideId] = useState<string | null>(null);

  const visible = entries.filter((entry) => entry.enabled);
  if (visible.length === 0) return null;

  const guideEntry = visible.find((entry) => entry.id === guideId) ?? null;
  const placement = featureFlags?.enableMediaAndSourceShare ? 'top-end' : 'bottom-end';
  const menuLabel = t('prototypes.menuLabel');

  return (
    <>
      <Menu
        isOpen={menuOpen}
        onOpenChange={setMenuOpen}
        placement={placement}
        title={menuLabel}
        content={(
          <>
            {visible.map((entry) => (
              <Fragment key={entry.id}>
                <MenuItem
                  checked={entry.loaded}
                  disabled={entry.loadDisabled}
                  description={entry.loaded ? entry.loadedDescription : entry.loadDescription}
                  onSelect={() => {
                    visible.forEach((other) => {
                      if (other.id !== entry.id && (other.loaded || other.hasData)) {
                        other.forget();
                      }
                    });
                    entry.load();
                    setGuideId(entry.id);
                  }}
                >
                  {entry.label}
                </MenuItem>
                <MenuItem
                  destructive
                  disabled={!entry.hasData}
                  description={entry.forgetDescription}
                  onSelect={() => {
                    entry.forget();
                    setGuideId((current) => (current === entry.id ? null : current));
                  }}
                >
                  {entry.forgetLabel}
                </MenuItem>
              </Fragment>
            ))}
          </>
        )}
      >
        {/* AGENT: do not restyle this trigger. Canonical circular inverse
            icon button — no className/prop overrides, no duotone. */}
        <Button
          type="button"
          {...statusBarIconButtonProps}
          aria-label={menuLabel}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className={statusBarIconButtonClassName}
          onClick={() => setMenuOpen((current) => !current)}
        >
          <FlaskIcon size={22} weight="regular" />
        </Button>
      </Menu>
      <Modal
        isOpen={guideEntry !== null}
        onOpenChange={(open) => {
          if (!open) setGuideId(null);
        }}
        title={guideEntry?.guideTitle}
        size="md"
        footer={(
          <Button
            type="button"
            size="sm"
            style="solid"
            color="accent"
            background="inverse"
            onClick={() => setGuideId(null)}
          >
            {t('prototypes.guideDismiss')}
          </Button>
        )}
      >
        {guideEntry?.guide ?? null}
      </Modal>
    </>
  );
}
